import type { IntegrationOrder } from '@/services/integrations.service';

/**
 * The prescriber's display name from an integration order's metadata
 * (`metadata.physician`: a combined `name`, else first + last). Shared by the
 * orders table, the Axiom page's Prescriber filter and the CSV export, so all
 * three agree on who the prescriber is.
 */
export function extractPrescriber(metadata: Record<string, unknown> | null): string | null {
  const phys = metadata?.physician as Record<string, unknown> | undefined;
  if (!phys) return null;
  const name = typeof phys.name === 'string' ? phys.name.trim() : '';
  if (name) return name;
  const combined = `${phys.firstName ?? ''} ${phys.lastName ?? ''}`.trim();
  return combined || null;
}

/**
 * The prescriber(s) of a whole order. Axiom keeps one on the order's metadata;
 * Partell keeps one per script (`items[].prescriber`), and they can differ, so
 * every distinct one is listed. The orders table, its sort and the CSV export
 * all read this, so they agree.
 */
export function orderPrescriber(order: Pick<IntegrationOrder, 'metadata' | 'items'>): string | null {
  const fromMeta = extractPrescriber(order.metadata as Record<string, unknown> | null);
  if (fromMeta) return fromMeta;
  const perScript = [...new Set((order.items ?? []).map((i) => (i.prescriber ?? '').trim()).filter(Boolean))];
  return perScript.length > 0 ? perScript.join('; ') : null;
}
